/**
 * Sub-admin permissions — one entry per sidebar tab. The sub-admin form has
 * one tick per tab, and a ticked tab means full access to that page.
 *
 * Internally a granted tab still carries view / edit / delete, which the route
 * guards and buttons ask about, but the three are always granted together (see
 * resolveActions).
 * Mirrors api.nabtt-ott/util/permissionCatalog.js; keep the two in step.
 */

// What can be allowed in a section, in display order.
export const PERMISSION_ACTIONS = ["view", "edit", "delete"];

export const PERMISSION_ACTION_LABELS = { view: "View", edit: "Edit", delete: "Delete" };

/**
 * Sections every sub-admin gets, permission or not.
 *
 * Dashboard is the landing page — a sub-admin who could not open it would be
 * bounced off it on login. Profile and Log Out were never permissions; the
 * sidebar always renders them.
 */
export const ALWAYS_GRANTED_MODULES = ["dashboard"];

/**
 * Sections that belong to the content-creator panel, not the admin sidebar.
 * A full ADMIN never sees Movies / Shorts / Web Series, so they are never
 * offered to a sub-admin either.
 */
export const CREATOR_ONLY_MODULES = ["movies", "shorts", "webSeries"];

// The sidebar tabs that used to live under the single `content` module.
export const CONTENT_MODULE_KEYS = [
  "movies",
  "shorts",
  "webSeries",
  "bulkUpload",
  "widgets",
  "banners",
];

// Modules that older sub-admin documents were saved against, and the tabs they
// grant now. Applied on read — nothing rewrites what is stored.
const LEGACY_MODULE_ALIASES = {
  content: CONTENT_MODULE_KEYS,
  sales: ["payouts"],
  revenueReports: ["payouts"],
  // Content Reports was one section before it was split into its three pages.
  reports: ["contentReports", "reportCategories", "reportReasons"],
};

/**
 * The view / edit / delete set a stored actions list stands for. A section is
 * granted whole — the form has one tick per section — so any row carrying an
 * action at all means all three. Mirrors resolveActions in the API catalog.
 */
const resolveActions = (actions) => {
  const list = (Array.isArray(actions) ? actions : [])
    .map((action) => String(action || "").trim())
    .filter(Boolean);
  return list.length ? [...PERMISSION_ACTIONS] : [];
};

/**
 * The one of view / edit / delete an action name asks for. Older names are
 * mapped by what they do: "read", "viewPayout" and "downloadReport" look,
 * "write", "uploadReport" and "manageAdvancePayment" change. No name at all
 * asks only for the section to be open.
 */
const normalizeAction = (action) => {
  const key = String(action || "").trim().toLowerCase();
  if (!key || key === "read" || key === "access") return "view";
  if (key.startsWith("view") || key.startsWith("download")) return "view";
  if (key === "delete") return "delete";
  return "edit";
};

/**
 * Stored { module, actions } rows as { moduleKey: ["view", "edit", …] }. A
 * legacy module is replaced by the tabs it became, so the old name itself
 * never appears — otherwise saving the form would send it back and re-grant
 * tabs the admin had just unticked. Used to read a sub-admin's permissions
 * back into the form and the list.
 */
export const permissionsToActionMap = (permissionsList = []) => {
  const granted = {};

  (Array.isArray(permissionsList) ? permissionsList : []).forEach((entry) => {
    const moduleKey = String(entry?.module || "").trim();
    if (!moduleKey) return;
    // Historical rows always carried actions; an empty one granted nothing.
    const actions = resolveActions(entry?.actions);
    if (!actions.length) return;

    (LEGACY_MODULE_ALIASES[moduleKey] || [moduleKey]).forEach((key) => {
      const merged = new Set([...(granted[key] || []), ...actions]);
      granted[key] = PERMISSION_ACTIONS.filter((action) => merged.has(action));
    });
  });

  return granted;
};

// Everything a sub-admin holds, including the sections nobody can be denied.
const grantedActions = (admin) => {
  const granted = permissionsToActionMap(admin?.permissions);
  ALWAYS_GRANTED_MODULES.forEach((moduleKey) => {
    granted[moduleKey] = [...PERMISSION_ACTIONS];
  });
  return granted;
};

export const hasPermission = (admin, module, action) => {
  if (!admin) return false;
  if (admin.role === "ADMIN") return true;

  const wanted = normalizeAction(action);
  const granted = grantedActions(admin);
  if (granted[module]?.includes(wanted)) return true;

  // A caller still naming a module that has since been split — "content" —
  // passes when any of the tabs it became allows the action.
  return (LEGACY_MODULE_ALIASES[module] || []).some((aliasKey) =>
    granted[aliasKey]?.includes(wanted)
  );
};

// Whether the section can be opened at all (shown in the sidebar).
export const canAccessModule = (admin, module) => hasPermission(admin, module, "view");

export const hasAnyPermission = (admin, checks = []) => {
  if (!admin) return false;
  if (admin.role === "ADMIN") return true;
  if (!checks.length) return false;

  return checks.some((check) =>
    typeof check === "string"
      ? canAccessModule(admin, check)
      : hasPermission(admin, check?.module, check?.action)
  );
};

export const canAccess = (admin, module) => canAccessModule(admin, module);

export const isAdmin = (admin) => {
  return admin?.role === "ADMIN";
};

export const isSubAdmin = (admin) => {
  return admin?.role === "SUB_ADMIN";
};
